import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useId, useState } from 'react';
/** KST는 일광절약시간이 없어 +09:00 고정이다. */
const KST_OFFSET = '+09:00';
function kstToday(now) {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(now));
}
function shiftDate(date, days) {
    const base = new Date(`${date}T00:00:00Z`);
    base.setUTCDate(base.getUTCDate() + days);
    return base.toISOString().slice(0, 10);
}
function kstEpoch(date, time) {
    return Date.parse(`${date}T${time}:00${KST_OFFSET}`);
}
/** datetime-local 값("YYYY-MM-DDTHH:MM")을 KST로 읽는다. */
export function parseKstLocal(value) {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value))
        return null;
    const epoch = Date.parse(`${value}:00${KST_OFFSET}`);
    return Number.isFinite(epoch) ? epoch : null;
}
export function toKstLocal(epoch) {
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(epoch));
    const get = (type) => parts.find((part) => part.type === type)?.value ?? '00';
    return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}
/** 칩이 고른 값을 실제 시각으로 바꾼다. 미래 시각은 null. */
export function resolveWhen(choice, custom, now = Date.now()) {
    const today = kstToday(now);
    if (choice === 'now')
        return now;
    if (choice === 'morning') {
        const at = kstEpoch(today, '08:00');
        return at <= now ? at : null;
    }
    if (choice === 'lastNight')
        return kstEpoch(shiftDate(today, -1), '22:00');
    const parsed = parseKstLocal(custom);
    return parsed != null && parsed <= now ? parsed : null;
}
export function useWhen(initial = 'now') {
    const [value, setValue] = useState(() => { const at = typeof location === 'undefined' ? null : new URLSearchParams(location.search).get('at'); return at && /^\d{4}-\d{2}-\d{2}$/.test(at) ? { choice: 'custom', custom: `${at}T12:00` } : { choice: initial, custom: '' }; });
    return [value, setValue, () => resolveWhen(value.choice, value.custom)];
}
/**
 * "어젯밤 기침을 아침에" 같은 지난 시각 기록을 모든 기록 폼에서 같은 방식으로 받는다.
 * 저장은 각 기록의 기존 시각 필드(occurredAt·measuredAt·servedAt·startedAt)에 그대로 들어간다.
 */
export function WhenField({ value, onChange, label = '언제 본 일인가요?' }) {
    const id = useId();
    const now = Date.now();
    const morningAvailable = kstEpoch(kstToday(now), '08:00') <= now;
    const resolved = resolveWhen(value.choice, value.custom, now);
    const future = value.choice === 'custom' && value.custom !== '' && resolved == null && parseKstLocal(value.custom) != null;
    const chips = [['now', '지금'], ...(morningAvailable ? [['morning', '오늘 아침']] : []), ['lastNight', '어젯밤'], ['custom', '직접']];
    return _jsxs("fieldset", { className: "when-field", children: [_jsx("legend", { children: label }), _jsx("div", { className: "choice-row", role: "group", "aria-label": label, children: chips.map(([choice, text]) => _jsx("button", { type: "button", className: value.choice === choice ? 'choice selected' : 'choice', "aria-pressed": value.choice === choice, onClick: () => onChange({ choice, custom: choice === 'custom' && !value.custom ? toKstLocal(now) : value.custom }), children: text }, choice)) }), value.choice === 'custom' ? _jsxs("label", { htmlFor: id, children: ["\uB0A0\uC9DC\uC640 \uC2DC\uAC01", _jsx("input", { id: id, type: "datetime-local", value: value.custom, max: toKstLocal(now), onChange: (event) => onChange({ choice: 'custom', custom: event.target.value }), "aria-invalid": future })] }) : null, future ? _jsx("p", { className: "field-error", role: "alert", children: "\uC544\uC9C1 \uC624\uC9C0 \uC54A\uC740 \uC2DC\uAC01\uC774\uC5D0\uC694." }) : null, value.choice === 'morning' ? _jsx("small", { children: "\uC624\uB298 \uC624\uC804 8\uC2DC\uB85C \uB0A8\uACA8\uC694." }) : value.choice === 'lastNight' ? _jsx("small", { children: "\uC5B4\uC81C \uBC24 10\uC2DC\uB85C \uB0A8\uACA8\uC694." }) : null] });
}
